import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Trash2, Pencil, LogOut, Plus, Check, X, Mail } from "lucide-react";

const EMPTY_PROJECT = {
  slug: "",
  index: 1,
  title: "",
  client: "",
  year: "",
  context: "",
  tagline: "",
  description: "",
  cover_image: "",
  tags: [],
  services: [],
  metrics: [],
  sub_projects: [],
  published: true,
};

function ProjectForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setList = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, index: Number(form.index) };
      if (form.id) {
        await api.put(`/admin/projects/${form.id}`, payload);
      } else {
        await api.post("/admin/projects", payload);
      }
      toast.success("Progetto salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-line p-8 space-y-6">
      <h3 className="font-heading text-3xl">{form.id ? "Modifica progetto" : "Nuovo progetto"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Slug" value={form.slug} onChange={set("slug")} required />
        <Field label="Index" type="number" value={form.index} onChange={set("index")} required />
        <Field label="Titolo" value={form.title} onChange={set("title")} required />
        <Field label="Cliente" value={form.client} onChange={set("client")} />
        <Field label="Anno" value={form.year} onChange={set("year")} />
        <Field label="Contesto" value={form.context} onChange={set("context")} />
      </div>
      <Field label="Tagline" value={form.tagline} onChange={set("tagline")} />
      <Field label="Cover image URL" value={form.cover_image} onChange={set("cover_image")} />
      <div>
        <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-2">Descrizione</label>
        <textarea
          rows={4}
          value={form.description}
          onChange={set("description")}
          className="w-full border border-line p-3 text-sm"
        />
      </div>
      <Field
        label="Tags (separati da virgola)"
        value={(form.tags || []).join(", ")}
        onChange={setList("tags")}
      />
      <Field
        label="Services (separati da virgola)"
        value={(form.services || []).join(", ")}
        onChange={setList("services")}
      />
      <div className="flex gap-3">
        <button
          type="submit"
          data-testid={ADMIN.saveProject}
          disabled={loading}
          className="bg-ink text-cream rounded-full px-6 py-3 text-xs uppercase tracking-widest hover:bg-peach"
        >
          {loading ? "Salvataggio…" : "Salva"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-6 py-3 text-xs uppercase tracking-widest border border-line"
        >
          Annulla
        </button>
      </div>
    </form>
  );
}

function Field({ label, ...rest }) {
  const testId = `admin-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-2">{label}</label>
      <input {...rest} data-testid={testId} className="w-full border border-line p-3 text-sm focus:outline-none focus:border-peach" />
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // project or {} for new

  const loadProjects = useCallback(
    () =>
      api
        .get("/admin/projects")
        .then((r) => setProjects(r.data))
        .catch((e) => toast.error(formatApiError(e))),
    []
  );

  const loadContacts = useCallback(
    () =>
      api
        .get("/admin/contacts")
        .then((r) => setContacts(r.data))
        .catch((e) => toast.error(formatApiError(e))),
    []
  );

  useEffect(() => {
    loadProjects();
    loadContacts();
  }, [loadProjects, loadContacts]);

  const deleteProject = async (id) => {
    if (!window.confirm("Eliminare il progetto?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Progetto eliminato");
      loadProjects();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}/read`);
      loadContacts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Eliminare il messaggio?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      loadContacts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-cream-alt">
      <header className="bg-white border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-heading text-2xl">
              Giovanni <span className="italic text-peach">Gabban</span>
            </Link>
            <span className="text-xs uppercase tracking-widest text-ink/50">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ink/60 hidden md:inline">{user?.email}</span>
            <button
              onClick={logout}
              data-testid={ADMIN.logout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-line-dark rounded-full px-4 py-2 hover:bg-ink hover:text-cream"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-4 border-b border-line mb-8">
          <button
            data-testid={ADMIN.tabProjects}
            onClick={() => setTab("projects")}
            className={`py-3 px-2 text-sm uppercase tracking-widest border-b-2 -mb-px ${
              tab === "projects" ? "border-peach text-peach" : "border-transparent text-ink/60"
            }`}
          >
            Progetti ({projects.length})
          </button>
          <button
            data-testid={ADMIN.tabContacts}
            onClick={() => setTab("contacts")}
            className={`py-3 px-2 text-sm uppercase tracking-widest border-b-2 -mb-px ${
              tab === "contacts" ? "border-peach text-peach" : "border-transparent text-ink/60"
            }`}
          >
            Messaggi ({contacts.length})
          </button>
        </div>

        {tab === "projects" && (
          <div className="space-y-6">
            {!editing && (
              <button
                onClick={() => setEditing({ ...EMPTY_PROJECT })}
                data-testid={ADMIN.newProject}
                className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-6 py-3 text-xs uppercase tracking-widest hover:bg-peach"
              >
                <Plus size={14} /> Nuovo progetto
              </button>
            )}

            {editing && (
              <ProjectForm
                initial={editing}
                onCancel={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  loadProjects();
                }}
              />
            )}

            <div className="bg-white border border-line">
              <table className="w-full text-sm">
                <thead className="bg-cream-alt border-b border-line text-xs uppercase tracking-widest text-ink/60">
                  <tr>
                    <th className="text-left p-4 w-16">#</th>
                    <th className="text-left p-4">Titolo</th>
                    <th className="text-left p-4 hidden md:table-cell">Contesto</th>
                    <th className="text-left p-4 hidden md:table-cell">Anno</th>
                    <th className="text-right p-4">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} className="border-t border-line">
                      <td className="p-4 font-heading text-xl">{String(p.index).padStart(2, "0")}</td>
                      <td className="p-4">
                        <div className="font-medium">{p.title}</div>
                        <div className="text-xs text-ink/50">/{p.slug}</div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-ink/70">{p.context}</td>
                      <td className="p-4 hidden md:table-cell text-ink/70">{p.year}</td>
                      <td className="p-4 text-right space-x-2">
                        <button
                          onClick={() => setEditing(p)}
                          data-testid={ADMIN.editProject(p.id)}
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-line rounded-full px-3 py-1.5 hover:border-peach hover:text-peach"
                        >
                          <Pencil size={12} /> Modifica
                        </button>
                        <button
                          onClick={() => deleteProject(p.id)}
                          data-testid={ADMIN.deleteProject(p.id)}
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-line rounded-full px-3 py-1.5 hover:border-red-500 hover:text-red-500"
                        >
                          <Trash2 size={12} /> Elimina
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="bg-white border border-line">
            {contacts.length === 0 && (
              <div className="p-12 text-center text-ink/50">Nessun messaggio ricevuto.</div>
            )}
            <ul className="divide-y divide-line">
              {contacts.map((c) => (
                <li key={c.id} className={`p-6 ${!c.read ? "bg-peach-soft/20" : ""}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-ink/50" />
                        <span className="font-medium">{c.name}</span>
                        {!c.read && (
                          <span className="text-[10px] uppercase tracking-widest bg-peach text-cream px-2 py-0.5 rounded-full">
                            Nuovo
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-ink/60">
                        <a href={`mailto:${c.email}`} className="editorial-link">{c.email}</a>
                        {c.company ? ` · ${c.company}` : ""}
                      </div>
                      <p className="mt-3 text-ink/80 max-w-2xl whitespace-pre-wrap">{c.message}</p>
                      <p className="mt-2 text-xs text-ink/40">
                        {new Date(c.created_at).toLocaleString("it-IT")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!c.read && (
                        <button
                          onClick={() => markRead(c.id)}
                          data-testid={`admin-contact-read-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-line rounded-full px-3 py-1.5 hover:border-peach hover:text-peach"
                        >
                          <Check size={12} /> Letto
                        </button>
                      )}
                      <button
                        onClick={() => deleteContact(c.id)}
                        data-testid={`admin-contact-delete-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-line rounded-full px-3 py-1.5 hover:border-red-500 hover:text-red-500"
                      >
                        <X size={12} /> Elimina
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
